import React, { useState } from "react";
import { Badge, Button, Chip, Paper, Stack } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import ShareIcon from "@mui/icons-material/Share";
import QueryYearPopover from "./QueryYearPopover";
import QueryTeamsPopover from "./QueryTeamsPopover";
import QueryTypeToggle from "./QueryTypeToggle";
import QueryGroupToggle from "./QueryGroupToggle";
import { isRecommendedFilterActive } from "../../utils/queryBuilder";

const QueryFilterBar = ({
  queryParams,
  catalog,
  availableTeams,
  isLoadingTeams,
  activeFilterCount = 0,
  onYearCommit,
  onTeamsCommit,
  onQueryTypeChange = () => {},
  onGroupChange = () => {},
  onRecommendedToggle = () => {},
  onOpenFilters,
  onShare,
  leagueScopeControl = null,
}) => {
  const [yearAnchorEl, setYearAnchorEl] = useState(null);
  const [teamsAnchorEl, setTeamsAnchorEl] = useState(null);
  const isTeamQuery = queryParams.queryType === "team";
  const teams = queryParams.teams || [];
  const hasGroups = catalog?.groups?.length > 0;
  const recommendedFilters = hasGroups
    ? (catalog.recommendedByGroup?.[queryParams.groupId] || [])
    : (catalog?.recommendedByGroup?.hitting || []);

  return (
    <Paper
      elevation={0}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 8,
        py: 1.5,
        mb: 2,
        bgcolor: "background.default",
      }}
    >
      {hasGroups && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ px: 0.5, pt: 1, pb: 0.5 }}
        >
          <QueryTypeToggle
            value={queryParams.queryType}
            onChange={onQueryTypeChange}
            queryTypes={catalog.queryTypes}
          />
          <QueryGroupToggle
            value={queryParams.groupId}
            groups={catalog.groups}
            onChange={onGroupChange}
          />
          {leagueScopeControl}
        </Stack>
      )}

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ overflowX: "auto", px: 0.5, pt: 1.25, pb: 0.5 }}
      >
        {!isTeamQuery && (
          <Chip
            label={`${queryParams.startYear}–${queryParams.endYear}`}
            clickable
            variant="outlined"
            onClick={(event) => setYearAnchorEl(event.currentTarget)}
            sx={{ fontWeight: 700, flexShrink: 0 }}
          />
        )}

        <Chip
          label={teams.length ? `${teams.length} team(s)` : "Teams"}
          clickable
          variant="outlined"
          color={teams.length ? "primary" : "default"}
          onClick={(event) => setTeamsAnchorEl(event.currentTarget)}
          sx={{ fontWeight: 700, flexShrink: 0 }}
        />

        {!isTeamQuery &&
          recommendedFilters.map((filter) => {
            const selected = isRecommendedFilterActive(queryParams, filter);
            return (
              <Chip
                key={filter.id}
                label={filter.abbrev}
                clickable
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                onClick={() => onRecommendedToggle(filter.id)}
                sx={{ fontWeight: 800, flexShrink: 0 }}
              />
            );
          })}

        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={onOpenFilters}
          sx={{ flexShrink: 0, textTransform: "none", fontWeight: 800 }}
        >
          <Badge badgeContent={activeFilterCount} color="primary">
            Filters
          </Badge>
        </Button>

        <Button
          variant="outlined"
          startIcon={<ShareIcon />}
          onClick={onShare}
          sx={{ flexShrink: 0, textTransform: "none", fontWeight: 800 }}
        >
          Share
        </Button>
      </Stack>

      <QueryYearPopover
        anchorEl={yearAnchorEl}
        startYear={queryParams.startYear}
        endYear={queryParams.endYear}
        min={catalog?.yearMin}
        max={catalog?.yearMax}
        onClose={() => setYearAnchorEl(null)}
        onCommit={onYearCommit}
      />
      <QueryTeamsPopover
        anchorEl={teamsAnchorEl}
        teams={teams}
        availableTeams={availableTeams}
        isLoadingTeams={isLoadingTeams}
        onClose={() => setTeamsAnchorEl(null)}
        onCommit={onTeamsCommit}
      />
    </Paper>
  );
};

export default QueryFilterBar;
